import { useCallback, useLayoutEffect, useMemo, useRef } from "react";

import { useSearchParams } from "react-router-dom";

/**
 * Qiymat o'zgarganda brauzer tarixida nima bo'ladi.
 *
 * `push` — tarixga YANGI yozuv qo'shiladi, ya'ni "orqaga" tugmasi avvalgi
 * filtrni qaytaradi. Holat, tab, ko'rinish, sahifa — hammasi odam ATAYLAB
 * qilgan tanlov, va uni bekor qilishning tabiiy yo'li shu.
 *
 * `replace` — manzil almashadi, lekin tarixga yozilmaydi. Bu debounce bilan
 * yoziladigan matn maydonlari uchun: har bir bosilgan harf uchun tarix yozuvi
 * qolsa, "orqaga" tugmasi avvalgi ekranga emas, o'sha qidiruvning oldingi
 * harfiga qaytarardi.
 */
export type HistoryMode = "push" | "replace";

/**
 * Bitta filtr qiymatini URL satriga va aksincha o'giradigan tipli kodek.
 *
 * Qiymat URL ga umuman yozilmasligi kerak bo'lganda (masalan, u standart
 * qiymatga teng) `serialize` `null` qaytaradi — ulashilgan havola shu tufayli
 * ortiqcha parametrlarsiz qoladi.
 */
export interface FilterParam<T> {
  defaultValue: T;
  parse: (raw: string | null) => T;
  serialize: (value: T) => string | null;
  /** Standarti — `push`; matn kodeki buni `replace` ga o'zgartiradi. */
  history?: HistoryMode;
}

/**
 * `T` qanday bo'lishidan qat'i nazar har bir `FilterParam<T>` qanoatlantiradigan
 * struktura. `serialize: (value: never)` turli tipdagi qiymatlardan yig'ilgan
 * sxemani `any` ga murojaat qilmasdan mos keladigan qiladi.
 */
type FilterParamLike = {
  defaultValue: unknown;
  parse: (raw: string | null) => unknown;
  serialize: (value: never) => string | null;
  history?: HistoryMode;
};

export type FilterSchema = Record<string, FilterParamLike>;

export type FilterValues<S extends FilterSchema> = {
  [K in keyof S]: S[K] extends FilterParam<infer T> ? T : never;
};

/** `patch`/`set`/`reset` uchun tarix xulqini bir marta bekor qilish. */
export interface FilterWriteOptions {
  history?: HistoryMode;
}

/** Sxemaning literal tiplarini `useUrlFilters` uchun saqlab qoladigan yordamchi. */
export const defineFilters = <S extends FilterSchema>(schema: S): S => schema;

/**
 * Bo'sh yoki faqat probeldan iborat qiymat URL ga umuman yozilmaydi.
 *
 * Standart tarix rejimi — `replace`: matn maydoni deyarli har doim debounce
 * bilan yoziladi va har bir harf uchun tarix yozuvi qoldirish "orqaga"
 * tugmasini ishlatib bo'lmaydigan qilardi.
 */
function stringParam(defaultValue = "", history: HistoryMode = "replace"): FilterParam<string> {
  return {
    defaultValue,
    history,
    parse: raw => raw ?? defaultValue,
    serialize: value => (value.trim() === "" ? null : value),
  };
}

// Bitta aniq qaytish tipi (`number | undefined`) argumentsiz chaqiruvni ham
// bir ma'noli qiladi — tip parametrining standartiga tayanishga hojat qolmaydi.
function numberParam(
  defaultValue?: number,
  history: HistoryMode = "push",
): FilterParam<number | undefined> {
  return {
    defaultValue,
    history,
    parse: raw => {
      if (raw === null || raw.trim() === "") return defaultValue;
      const parsed = Number(raw);
      return Number.isFinite(parsed) ? parsed : defaultValue;
    },
    serialize: value => (typeof value !== "number" || Number.isNaN(value) ? null : String(value)),
  };
}

function booleanParam(
  defaultValue?: boolean,
  history: HistoryMode = "push",
): FilterParam<boolean | undefined> {
  return {
    defaultValue,
    history,
    parse: raw => {
      if (raw === "true") return true;
      if (raw === "false") return false;
      return defaultValue;
    },
    // Standart qiymat URL ga yozilmaydi: `?featured=false` kabi hech narsani
    // o'zgartirmaydigan quyruq havolani uzaytirardi.
    serialize: value =>
      typeof value === "boolean" && value !== defaultValue ? String(value) : null,
  };
}

// Qaytish tipi ataylab `T | ""` (standarti `""`): aks holda bo'sh qiymat
// belgisi tip parametrining standartidan kelib chiqib yo'qolib ketardi.
function enumParam<T extends string>(
  values: readonly T[],
  defaultValue: T | "" = "",
  history: HistoryMode = "push",
): FilterParam<T | ""> {
  return {
    defaultValue,
    history,
    parse: raw =>
      raw !== null && (values as readonly string[]).includes(raw) ? (raw as T) : defaultValue,
    serialize: value => (value === defaultValue || value === "" ? null : value),
  };
}

/** Ro'yxat sahifalarida uchraydigan filtr qiymat turlari uchun tayyor kodeklar. */
export const param = {
  string: stringParam,
  number: numberParam,
  boolean: booleanParam,
  enum: enumParam,
};

/**
 * Bitta yozuvning tarix rejimi.
 *
 * Aniq berilgan rejim ustun turadi. Aks holda o'zgarayotgan kalitlarga
 * qaraladi: ular orasida bitta ham `push` bo'lsa — `push`. Ya'ni qidiruv
 * bilan birga holat ham almashsa, bu odamning tanlovi hisoblanadi va
 * tarixga tushadi.
 */
function resolveHistory<S extends FilterSchema>(
  schema: S,
  keys: readonly (keyof S & string)[],
  explicit?: HistoryMode,
): HistoryMode {
  if (explicit) return explicit;
  if (keys.length === 0) return "replace";
  return keys.some(key => (schema[key]?.history ?? "push") === "push") ? "push" : "replace";
}

/**
 * Filtr holatini URL so'rov satridan o'qiydi va unga yozadi: shu tufayli
 * filtrlarni havola bilan ulashsa bo'ladi, ular sahifa yangilanganda va orqaga
 * qaytganda saqlanadi hamda React Query kalitlari bilan bir xil qoladi. Sxemada
 * `page` bo'lsa, `page` dan boshqa filtr o'zgarganda sahifa birinchisiga qaytadi.
 */
export function useUrlFilters<S extends FilterSchema>(schema: S) {
  const [searchParams, setSearchParams] = useSearchParams();

  const values = useMemo(() => {
    const result = {} as FilterValues<S>;
    for (const key of Object.keys(schema) as (keyof S)[]) {
      result[key] = schema[key].parse(
        searchParams.get(key as string),
      ) as FilterValues<S>[typeof key];
    }
    return result;
  }, [schema, searchParams]);

  /**
   * Oxirgi YOZILGAN parametrlar.
   *
   * React Router'dagi `setSearchParams(prev => …)` ning `prev` i — render
   * paytidagi qiymat: bir tikda ketma-ket ikki yozuv (jadval pastidagi sahifa
   * hajmi, keyin sahifa raqami) bir-birining ustidan yozilar va birinchisi
   * jimgina yo'qolardi. Ref har yozuvda darhol yangilanadi, keyingi render esa
   * uni haqiqiy manzil bilan tenglaydi — "orqaga" bosilganda ham.
   */
  const latest = useRef(searchParams);
  // Shu tikda allaqachon yozilganmi: ikkinchi yozuv tarixga YANGI yozuv qo'shmaydi.
  const writtenThisTick = useRef(false);

  useLayoutEffect(() => {
    latest.current = searchParams;
  }, [searchParams]);

  const write = useCallback(
    (mutate: (params: URLSearchParams) => void, history: HistoryMode) => {
      const params = new URLSearchParams(latest.current);
      mutate(params);

      // Birinchi yozuv tarixga kirgan bo'lsa, shu tikdagi keyingisi uni
      // almashtiradi: bitta harakat — bitta "orqaga".
      const replace = writtenThisTick.current || history === "replace";
      latest.current = params;
      writtenThisTick.current = true;
      queueMicrotask(() => {
        writtenThisTick.current = false;
      });

      setSearchParams(params, { replace });
    },
    [setSearchParams],
  );

  // Bir nechta kalitni bitta navigatsiyada qo'llaydi.
  const patch = useCallback(
    (partial: Partial<FilterValues<S>>, options?: FilterWriteOptions) => {
      const keys = Object.keys(partial) as (keyof S & string)[];

      write(
        params => {
          let changedNonPage = false;
          for (const key of keys) {
            const serialized = schema[key].serialize(partial[key] as never);
            if (serialized === null) params.delete(key);
            else params.set(key, serialized);
            if (key !== "page") changedNonPage = true;
          }
          // Sxema sahifalarga bo'linsa, har qanday filtr o'zgarishi birinchisiga qaytaradi.
          if (changedNonPage && "page" in schema) params.delete("page");
        },
        resolveHistory(schema, keys, options?.history),
      );
    },
    [schema, write],
  );

  const set = useCallback(
    <K extends keyof S & string>(key: K, next: FilterValues<S>[K], options?: FilterWriteOptions) =>
      patch({ [key]: next } as Partial<FilterValues<S>>, options),
    [patch],
  );

  // Tozalash ham ataylab qilingan tanlov: standarti `push`, ya'ni "orqaga"
  // bosilsa filtrlar qaytadi.
  const reset = useCallback(
    (options?: FilterWriteOptions) => {
      write(params => {
        for (const key of Object.keys(schema)) params.delete(key);
      }, options?.history ?? "push");
    },
    [schema, write],
  );

  return { values, set, patch, reset };
}
